import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { components } from '../api/generated/schema'
import { keys } from '../api/keys'
import type { AsyncState } from '../platform/async/Async'
import { fromQuery } from '../platform/async/fromQuery'

/**
 * The single read of `GET /api/v1/notifications`.
 *
 * The drawer and the sidebar badges are two consumers of ONE cache entry, not
 * two fetches. That is the whole reason #191 and #192 were split apart: a
 * badge counting `is_read` client-side and a drawer reading the server's
 * `unread_count` produce two numbers that drift, and neither is wrong enough
 * to notice.
 *
 * `unread_count` is the server's, always. Counting unread items in the
 * response would be a different number the moment the list is paginated —
 * which it is: `page`/`page_size` are query parameters on this endpoint.
 */
export type Notification = components['schemas']['NotificationResponse']
export type NotificationList = components['schemas']['NotificationList']

/** Distinguishable in a log from a render-time failure. The message is the
 *  wire failure; the user-facing copy is chosen in `useNotifications`. */
export class NotificationsError extends Error {
  constructor(cause: unknown) {
    super('notifications-unavailable', { cause })
    this.name = 'NotificationsError'
  }
}

async function listNotifications(): Promise<NotificationList> {
  const { data, error } = await api.GET('/api/v1/notifications')
  if (error !== undefined || data === undefined) throw new NotificationsError(error)
  return data
}

/** One place the key and the fetcher are paired, so the two observers below
 *  cannot drift onto different keys — which would be two fetches again. */
const listQuery = { queryKey: keys.notifications.list(), queryFn: listNotifications } as const

/**
 * Four states, not five. Per D20 stale is bank-sync freshness reported by the
 * server; notifications have no upstream sync, so no `asOf` is passed and the
 * stale branch is unreachable here by construction. Deriving it from
 * `dataUpdatedAt` would show the user when WE last fetched and call it the
 * date the data is from.
 */
export function useNotifications(): AsyncState<NotificationList> {
  const query = useQuery(listQuery)
  return fromQuery(query, {
    message: () => 'Notifications could not be loaded.',
    isEmpty: (list) => list.notifications.length === 0,
    recovery: {
      label: 'Try again',
      onAct: () => {
        void query.refetch()
      },
    },
  })
}

/**
 * The same query, through a `select`. TanStack sees one key with two
 * observers and fetches once; a second `queryFn` on a second key would be the
 * defect this module exists to prevent.
 *
 * `0` before the first response rather than `undefined`: the caller renders
 * this into a badge, and `undefined` reaches the screen as blank.
 */
export function useUnreadCount(): number {
  const { data } = useQuery({ ...listQuery, select: (list: NotificationList) => list.unread_count })
  return data ?? 0
}

/**
 * Refetch, for the empty state's one action.
 *
 * Through the same key rather than a second call to `listNotifications`: a
 * direct call would bypass the cache both consumers read, so the drawer would
 * update and the sidebar badge would not.
 */
export function useRefreshNotifications(): () => void {
  const client = useQueryClient()
  return () => {
    void client.refetchQueries({ queryKey: keys.notifications.list() })
  }
}

/**
 * Invalidates `notifications/list` — the narrowest key covering what changed.
 * The item's `is_read` and the response's `unread_count` both live in that one
 * response. Invalidating `keys.notifications.all()` would also be correct and
 * wider, and the root would refetch details this module does not have.
 */
export function useMarkRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: Notification['id']) => {
      // `notification_id`, not `id`: that is how the path parameter is spelled.
      const { error } = await api.PATCH('/api/v1/notifications/{notification_id}/read', {
        params: { path: { notification_id: id } },
      })
      if (error !== undefined) throw new NotificationsError(error)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notifications.list() }),
  })
}

/** `POST /read-all` returns 204 with no body, so there is no `data` to check —
 *  only `error`. Treating an absent body as a failure would fail every call. */
export function useMarkAllRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/notifications/read-all')
      if (error !== undefined) throw new NotificationsError(error)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notifications.list() }),
  })
}
